import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { brokerOutreachApi, type BrokerOutreachWordingDto } from "@/api/brokerOutreach";
import { Button, Skeleton, Textarea, useToast } from "@/design-system/components/ui";
import { brokerOutreachWordingKeys } from "@/hooks/buyers/buyerKeys";
import { InlineQueryError } from "../queryStates";

/**
 * The fallback wording behind the text, web form and follow-up text cards (R9)
 * — the deterministic message the server ships when the assistant fails or a
 * draft fails its checks. Team default only; `Communications.ManageEmailTemplates`
 * (the Outreach tab's wording gate), so the host renders this only when the
 * state says the reader holds it.
 *
 * <p>🔴 <b>Publishing is checked for placeholders only.</b> The server refuses a
 * token outside the template's allow-list (there is no money token on the
 * list); it runs the draft checks — role claims, figures, contact details,
 * buyer claims — at RENDER, and a published wording that fails them is replaced
 * by the built-in default for that draft. There is no save-time phrase check,
 * which is why this panel says so rather than implying one.</p>
 *
 * <p>The follow-up EMAIL's fallback is not here: it is an email template, edited
 * in the email template admin like every other.</p>
 */
export function BrokerOutreachWordingPanel() {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="rounded-md border border-border-light px-space-4 py-space-3"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      data-testid="broker-outreach-wording"
    >
      <summary className="cursor-pointer text-small font-medium">Fallback wording (admins)</summary>
      {open ? <WordingList /> : null}
    </details>
  );
}

function WordingList() {
  const list = useQuery({
    queryKey: brokerOutreachWordingKeys.all,
    queryFn: () => brokerOutreachApi.listWording(),
  });

  if (list.isLoading) return <Skeleton className="mt-space-3 h-24 w-full" />;
  if (list.isError || !list.data) {
    return (
      <div className="mt-space-3">
        <InlineQueryError what="the fallback wording" onRetry={() => void list.refetch()} retrying={list.isFetching} />
      </div>
    );
  }
  return (
    <div className="mt-space-3 grid gap-space-5">
      <p className="text-caption text-gray-mid m-0">
        Used when the assistant&apos;s draft is unavailable or fails the checks. Only the placeholders listed under each
        are accepted. The draft checks run when a message is written: a published wording that fails them is replaced
        by the built-in default for that draft.
      </p>
      {list.data.map((t) => (
        <WordingEditor key={`${t.key}:${t.version}`} template={t} />
      ))}
    </div>
  );
}

function WordingEditor({ template }: { template: BrokerOutreachWordingDto }) {
  const qc = useQueryClient();
  const toast = useToast();
  const current = template.publishedText ?? template.defaultText;
  const [text, setText] = useState(current);
  const dirty = text.trim() !== current.trim();

  // Only the list is stale after a publish or a reset: a fallback is read when
  // the NEXT draft is written, and no cached draft changes.
  const publish = useMutation({
    mutationFn: () => brokerOutreachApi.publishWording(template.key, text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: brokerOutreachWordingKeys.all });
      toast.success(`${template.name}: published.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reset = useMutation({
    mutationFn: () => brokerOutreachApi.resetWording(template.key),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: brokerOutreachWordingKeys.all });
      toast.success(`${template.name}: back to the default.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="grid gap-space-2" data-testid={`broker-outreach-wording-${template.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <span className="text-small font-medium">{template.name}</span>
        <span className="text-caption text-gray-mid">
          {template.publishedText ? `Published (version ${template.version})` : "Built-in default"}
        </span>
      </div>
      <p className="text-caption text-gray-mid m-0">{template.description}</p>
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} aria-label={`${template.name} wording`} />
      <p className="text-caption text-gray-mid m-0">
        Placeholders: {template.allowedTokens.map((t) => `{{${t}}}`).join(", ")}
      </p>
      <div className="flex flex-wrap items-center gap-space-3">
        <Button size="sm" onClick={() => publish.mutate()} disabled={!dirty || !text.trim() || publish.isPending}>
          {publish.isPending ? "Publishing…" : "Publish"}
        </Button>
        {template.publishedText ? (
          <Button size="sm" variant="text" onClick={() => reset.mutate()} disabled={reset.isPending}>
            Reset to default
          </Button>
        ) : null}
      </div>
    </div>
  );
}
